/**
 * CommunityCard — the shared community BROWSE card (DEV-1447).
 *
 * Extracted from CommunitiesPage.tsx's inline renderCard so the Discover
 * Communities catalog and CommunitiesPage cannot drift. Adds the Follow CTA
 * (the catalog's whole reason for existing — it feeds the follow-3 rung basic)
 * and an optional member count.
 *
 * Accessibility note — the original was a <button> wrapping the whole card,
 * which cannot hold a nested <FollowButton> (nested interactive elements are
 * invalid and break keyboard/AT navigation). This uses the stretched-hit-area
 * pattern instead: a positioned <button> covers the card for the navigate
 * action, and the Follow control sits above it on its own stacking layer.
 */

import React from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { mediaSrc } from '../../api/mediaUrl';
import FollowButton from '../FollowButton/FollowButton';
import {
  findCountryFlag,
  resolveAncestryLine,
  resolveCardHero,
  placeholderHeroGradient,
  type CommunityEntity,
  type CommunityRelationship,
} from './communityCardData';
import styles from './CommunityCard.module.css';

export type { CommunityEntity, CommunityRelationship };

interface Props {
  entity: CommunityEntity;
  entityMap: Map<string, CommunityEntity>;
  parentMap: Map<string, string>;
  onOpen: (communityGuid: string) => void;
  /** Render the Follow CTA. Default true. */
  showFollow?: boolean;
  /**
   * Seeds FollowButton so it skips its own status round-trip. Pass from a
   * caller that already holds the user's followed set (useApiMyFollows).
   */
  initialFollowing?: boolean;
  /** Member count shown in the footer. Omit to hide. */
  memberCount?: number;
  /**
   * 'large' — the LEAD shelf's card (DEV-1447, Skip 2026-07-13).
   *
   * The catalog was a uniform wall of small cards with no focal point. The lead
   * shelf now renders 3–4 large cards to break the rhythm. This also serves the
   * signature element: place photography is what makes the catalog identifiable
   * (the 1-second test), and small cards waste it.
   *
   * The rule is POSITIONAL, not per-shelf — whichever shelf leads renders large.
   * That way no shelf is special-cased, and the treatment degrades gracefully
   * when an empty shelf hides and the lead position shifts.
   */
  size?: 'default' | 'large';
}

const CommunityCard: React.FC<Props> = ({
  entity,
  entityMap,
  parentMap,
  onOpen,
  showFollow = true,
  initialFollowing,
  memberCount,
  size = 'default',
}) => {
  const { t } = useAppTranslation('pages');

  /**
   * The hero is GUARANTEED — own photo → nearest ancestor's photo → a designed
   * place-treatment. A community is never hidden for lacking an image, and a
   * card is never a gray box. See the long note in communityCardData.ts for why
   * gating listing on a photo was rejected.
   */
  const hero = resolveCardHero(entity, entityMap, parentMap);
  const heroUrl = mediaSrc(hero.url);
  const flag = findCountryFlag(entity, entityMap, parentMap);
  const ancestry = resolveAncestryLine(entity, entityMap, parentMap);
  const name = entity.name || t('communities.unnamed');
  const hasFooter = showFollow || memberCount !== undefined;

  return (
    // Every card is now a hero card — the treatment is uniform whether the image
    // is a photograph or the generated fallback.
    <article
      className={[styles.card, styles.cardWithHero, size === 'large' ? styles.cardLarge : '']
        .filter(Boolean)
        .join(' ')}
    >
      <div className={styles.cardHero}>
        {heroUrl ? (
          /* Offscreen cards do not fetch their hero. The catalog renders every
             matching community into the DOM, so at a few hundred communities
             this is the difference between a handful of image requests and all
             of them. It does NOT solve the real scale problem — the grid still
             mounts every card, and the entity + membership payloads are still
             fetched wholesale — but it is the cheapest real win, and correct
             regardless of what the paging fix turns out to be. */
          <img
            src={heroUrl}
            alt=""
            className={styles.cardHeroImg}
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div
            className={styles.cardHeroFallback}
            style={{ backgroundImage: placeholderHeroGradient(entity.entity_guid) }}
            aria-hidden="true"
          />
        )}
      </div>

      {/* An inherited photo is still a picture of where you are — just at a
          coarser grain. Saying so keeps it honest, rather than passing off a
          photo of Rhode Island as a photo of Pawtucket. */}
      {hero.inheritedFrom && (
        <span className={styles.heroCredit} title={t('communities.heroInherited', { name: hero.inheritedFrom })}>
          {hero.inheritedFrom}
        </span>
      )}

      {/* Stretched hit area — the card's navigate action. Sits under the
          footer controls so the Follow CTA stays independently clickable. */}
      <button
        type="button"
        className={styles.cardLink}
        onClick={() => onOpen(entity.entity_guid)}
        aria-label={name}
        title={name}
      />

      <div className={styles.cardBody}>
        <div className={styles.cardTitle}>
          {flag && <span className={styles.cardFlag}>{flag}</span>}
          <span className={styles.cardName}>{name}</span>
        </div>
        {ancestry && <div className={styles.cardAncestry}>{ancestry}</div>}

        {hasFooter && (
          <div className={styles.cardFooter}>
            <span className={styles.cardMeta}>
              {memberCount !== undefined
                ? t('communities.memberCount', { count: memberCount })
                : ''}
            </span>
            {showFollow && (
              <span className={styles.followSlot}>
                <FollowButton
                  targetType="community"
                  targetGuid={entity.entity_guid}
                  compact
                  showCount={false}
                  initialFollowing={initialFollowing}
                />
              </span>
            )}
          </div>
        )}
      </div>
    </article>
  );
};

export default CommunityCard;
